import { useCallback, useState } from "react";
import { t } from "../i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { ScanFace } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PinPad } from "../components/PinPad";
import { LangButton } from "../components/LangButton";

export default function Bloqueo() {
  const { user, unlockSuccess, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [errorTick, setErrorTick] = useState(0);
  const [busy, setBusy] = useState(false);

  const go = useCallback(() => {
    const dest = location.state?.from && location.state.from !== "/bloqueo" ? location.state.from : "/";
    navigate(location.pathname === "/bloqueo" ? dest : location.pathname, { replace: true });
  }, [navigate, location]);

  const verify = useCallback(async (pin) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/pin/verify", { pin });
      unlockSuccess(data);
      go();
    } catch (err) {
      setError(errorMessage(err, t("pin.wrong")));
      setErrorTick((t) => t + 1);
    } finally { setBusy(false); }
  }, [unlockSuccess, go]);

  const biometric = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const { data: options } = await api.post("/webauthn/login/options");
      const credential = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post("/webauthn/login/verify", { credential });
      unlockSuccess(data);
      go();
    } catch (err) {
      setError(err?.name === "NotAllowedError" ? t("pin.cancelled") : errorMessage(err, t("pin.bioFail")));
    } finally { setBusy(false); }
  }, [unlockSuccess, go]);

  return (
    <div data-testid="lock-screen" className="min-h-[100dvh] max-w-[480px] mx-auto flex flex-col items-center px-6 pt-[max(56px,env(safe-area-inset-top))] pb-10 fade-up">
      <div className="w-full flex justify-end -mt-6 mb-2"><LangButton /></div>
      <span className="w-12 h-12 rounded-full border-[4px] border-[#d4af37] mb-6" aria-hidden />
      <h1 className="text-2xl font-medium mb-1">{t("pin.enter")}</h1>
      <p className="text-sm text-muted-foreground mb-10">{user?.email}</p>

      <PinPad onComplete={verify} onBiometric={biometric} showBiometric={!!user?.has_webauthn} error={errorTick} disabled={busy} />

      <div className="h-6 mt-6">
        {error && <p data-testid="pin-error" className="text-sm text-[#f43f5e] text-center">{error}</p>}
      </div>

      {user?.has_webauthn && (
        <button data-testid="biometric-unlock-btn" onClick={biometric} disabled={busy}
          className="mt-4 w-full max-w-[300px] rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#f3c649] py-4 text-base font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <ScanFace size={22} /> {t("pin.useBiometric")} </button>
      )}

      <button data-testid="lock-logout-btn" onClick={logout} className="mt-auto pt-8 text-sm text-muted-foreground hover:text-foreground">{t("common.logout")} </button>
    </div>
  );
}
